import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

const SG_LAUNCH_PROMO_PRODUCT_IDS = new Set([
  '10314104537401',
  '10316786237753',
  '10318130381113',
  '10318223900985',
]);
const SG_LAUNCH_PROMO_MULTIPLIER = 0.63;

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#renderMercadoPagoInstallments();

    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Handles product select event and updates the price.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const { priceContainer, volumePricingNote } = this.refs;
        const newProductPrice = html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
        if (!newProductPrice) return;

        const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
        if (newPrice && priceContainer) {
          priceContainer.replaceWith(newPrice);
        }

        const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
        if (!newNote) {
          volumePricingNote?.remove();
        } else if (!volumePricingNote) {
          newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
        } else {
          volumePricingNote.replaceWith(newNote);
        }

        const currentMercadoPago = this.querySelector('[data-mp-installments]');
        const newMercadoPago = newProductPrice.querySelector('[data-mp-installments]');

        if (currentMercadoPago && newMercadoPago) {
          currentMercadoPago.replaceWith(newMercadoPago.cloneNode(true));
        } else if (!currentMercadoPago && newMercadoPago) {
          const updatedPrice = this.querySelector('[ref="priceContainer"]');
          updatedPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newMercadoPago.cloneNode(true)));
        } else if (currentMercadoPago && !newMercadoPago) {
          currentMercadoPago.remove();
        }

        this.#renderMercadoPagoInstallments();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-price] Event promise rejected:', error);
      });
  };

  #renderMercadoPagoInstallments() {
    const container = /** @type {HTMLElement|null} */ (this.querySelector('[data-mp-installments]'));
    const text = container?.querySelector('[data-mp-installments-text]');
    if (!container || !text) return;

    container.querySelector('.sg-mp-installments__badge')?.remove();

    let divider = /** @type {HTMLElement|null} */ (container.querySelector('.sg-installment-divider'));
    if (!divider) {
      divider = document.createElement('span');
      divider.className = 'sg-installment-divider';
      divider.setAttribute('aria-hidden', 'true');
      container.prepend(divider);
    }

    const alignment = getComputedStyle(this).textAlign;
    const alignItems = alignment === 'center' ? 'center' : alignment === 'right' ? 'flex-end' : 'flex-start';

    container.style.display = 'flex';
    container.style.flexDirection = 'column';
    container.style.alignItems = alignItems;
    container.style.gap = '0.32rem';
    container.style.marginTop = '0.45rem';

    divider.style.display = 'block';
    divider.style.width = '32px';
    divider.style.height = '2px';
    divider.style.borderRadius = '999px';
    divider.style.background = '#ff4315';
    divider.style.flex = '0 0 auto';

    const basePriceCents = Number(container.dataset.priceCents || 0);
    const productId = String(this.dataset.productId || '').split('/').pop();
    const priceCents = SG_LAUNCH_PROMO_PRODUCT_IDS.has(productId)
      ? Math.round(basePriceCents * SG_LAUNCH_PROMO_MULTIPLIER)
      : basePriceCents;
    const maxInstallments = 5;

    if (!priceCents) {
      text.textContent = `ou ${maxInstallments}x sem juros`;
      return;
    }

    const installmentValue = priceCents / 100 / maxInstallments;
    const currency = container.dataset.currency || 'BRL';

    let formattedInstallment;
    try {
      formattedInstallment = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(installmentValue);
    } catch {
      formattedInstallment = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(installmentValue);
    }

    text.textContent = `ou ${maxInstallments}x de ${formattedInstallment} sem juros`;
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
